import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { PageContentApiP1Response } from "../../../../../../api/services/pageContent/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiButtonTertiary,
  MuiDoubleLabel,
} from "../../../../../../components";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import { MODULE_PERMISSIONS } from "../../../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../../../helpers/constants/componentIds";
import { getValueOrBlank, isNotEmpty } from "../../../../../../helpers/utils";
import useAccordion from "../../../../../../hooks/useAccordion";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import { RegistrationDetail } from "../../../../../createProgram/components/createProgramGeneralDetail/inputDetailTab/registrationDetail/RegistrationDetail";
import { AboutParticipant } from "../../../../../createProgram/components/createProgramTabs/tabs/participant/components/aboutParticipant/AboutParticipant";
import { coachAssignmentSiteCoreKeys } from "../../../../../createProgram/components/createProgramTabs/tabs/participant/components/coachAssignment/SiteCoreKeys";
import ParticipantActivityTimeline from "../../../../../createProgram/components/createProgramTabs/tabs/participant/components/participantActivityTimeline/ParticipantActivityTimeline";
import { GoalSelection } from "../../../../../createProgram/components/createProgramTabs/tabs/participant/components/participantGoalSelection/GoalSelection";
import { goalSelectionSiteCoreKeys } from "../../../../../createProgram/components/createProgramTabs/tabs/participant/components/participantGoalSelection/SiteCoreKeys";
import {
  ParticipantDetail,
  ProgramDetail,
} from "../../../../../createProgram/modelsAndSchemas/Models";
import {
  activityTimelineSiteCoreKeys,
  programSiteCoreKeys,
} from "../../../../../createProgram/siteCore/SiteCoreKeys";
import { CareerGoals } from "./components/careerGoals/CareerGoals";
import { ContactAndNotification } from "./components/contactAndNotification/ContactAndNotification";
import { OutreachDetails } from "./components/outreachDetails/OutreachDetails";
import { ParticipantDetails } from "./components/participantDetails/ParticipantDetails";
import { ParticipantIntakeSelection } from "./components/participantIntakeSelection/ParticipantIntakeSelection";
import { SponsoringEmployer } from "./components/sponsoringEmployer/SponsoringEmployer";
import { ViewCoachAssignment } from "./components/viewCoachAssignment/viewCoachAssignment";
import { TabsWrapper } from "./tabs.style";

interface ParticipantTabProp {
  pageId: string;
  participantDetail?: ParticipantDetail;
  pageContentData?: PageContentApiP1Response;
  nextPersona?: string;
  isCMCProgram?: boolean;
  deliveryCountry?: string;
  isTMAssessmentProgram?: boolean;
  isTMLeaderCoachingProgram?: boolean;
  isFromCoachPortal?: boolean;
  programDetail?: ProgramDetail;
  programId?: string;
}
const initialAccordionStatus = {
  all: true,
  addressDetails: true,
  sponsoringEmployer: true,
  participantInformation: true,
  outreachDetails: true,
  careerGoals: true,
  participantActivityTimeline: true,
  coachAssignment: true,
  goalSelection: true,
  outreach: true,
  contactAndNotification: true,
  intakeSelection: true,
};

export const ParticipantTab: FC<ParticipantTabProp> = ({
  pageId,
  participantDetail,
  pageContentData,
  nextPersona,
  isCMCProgram,
  deliveryCountry,
  isTMAssessmentProgram,
  isTMLeaderCoachingProgram,
  isFromCoachPortal = false,
  programDetail,
  programId,
}) => {
  const {
    fetchComponentTitle,
    commonLabels,
    fetchLabel,
    fetchDropdownListKeyValueForCountrySpecific,
  } = useSiteCore();

  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
  } = useAccordion(initialAccordionStatus);
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={fetchComponentTitle(labelKey)} />;
  };
  const getRemoveIcon = () => {
    return <RemoveIcon color={"primary"} />;
  };

  const getAddIcon = () => {
    return <AddIcon color={"primary"} />;
  };

  const { careerGoal } = programSiteCoreKeys;

  const { activityTimeline } = activityTimelineSiteCoreKeys;

  const countrySpecificParticipantDetail = useMemo(() => {
    const stateRegionValue = fetchDropdownListKeyValueForCountrySpecific(
      programSiteCoreKeys.addressDetails,
      programSiteCoreKeys.stateRegionProvinceListPrefix,
      getValueOrBlank(participantDetail?.countryId),
      getValueOrBlank(participantDetail?.stateId),
    );

    return {
      stateRegionValue: isNotEmpty(stateRegionValue)
        ? stateRegionValue
        : participantDetail?.stateId,
    };
  }, [participantDetail?.countryId, participantDetail, siteCoreLoading]);

  const formatAddress = () => {
    const addressParts = [
      participantDetail?.address1,
      participantDetail?.address2,
      participantDetail?.address3,
      participantDetail?.city,
      countrySpecificParticipantDetail.stateRegionValue,
      participantDetail?.countryName ?? participantDetail?.countryId,
      participantDetail?.postalCode,
    ].filter((part) => part?.trim());
    if (addressParts.length === 0) return "--";
    return addressParts.length === 1
      ? addressParts[0]
      : `${addressParts.slice(0, -1).join(", ")} - ${addressParts.at(-1)}`;
  };

  const getPreferredLanguageName = (
    pageContentData: any,
    preferredLanguageCode?: string,
  ): string => {
    if (!preferredLanguageCode) return "";

    const allLanguages =
      pageContentData?.MarketDetails?.flatMap(
        (market: {
          Supportedlanguage: { ISOCode: string; LanguageName: string }[];
        }) => market.Supportedlanguage || [],
      ) || [];

    const matched = allLanguages.find(
      (lang: { ISOCode: string }) => lang.ISOCode === preferredLanguageCode,
    );

    return matched?.LanguageName || "";
  };
  const preferredLanguageName = getPreferredLanguageName(
    pageContentData,
    participantDetail?.preferredLanguage,
  );
  return (
    <>
      <MuiBox className="header-collapse">
        <MuiButtonTertiary
          id={COMPONENT_IDS.EXPAND_COLLAPSE_BUTTON}
          onClick={handleExpandCollapseAllChange}
        >
          {accordionStatus.all
            ? commonLabels.collapseAll
            : commonLabels.expandAll}
          {accordionStatus.all ? getRemoveIcon() : getAddIcon()}
        </MuiButtonTertiary>
      </MuiBox>
      <TabsWrapper>
        {isFromCoachPortal && isNotEmpty(programDetail?.registrationCode) && (
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={
              <AccordionHeaderText
                label={fetchLabel(
                  programSiteCoreKeys.generalDetails,
                  programSiteCoreKeys.registrationDetails,
                )}
              />
            }
            onChange={() =>
              handleIndividualAccordionChange("registrationDetails")
            }
            isExpanded={accordionStatus.registrationDetails}
          >
            <MuiBox className="content">
              <RegistrationDetail programDetail={programDetail} />
            </MuiBox>
          </AccordionHeader>
        )}

        <PermissionWrapper
          permission={
            isFromCoachPortal
              ? MODULE_PERMISSIONS.COACH_PORTAL_VIEW_PARTICIPANT_INFORMATION
              : MODULE_PERMISSIONS.VIEW_PARTICIPANT_INFORMATION
          }
        >
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(programSiteCoreKeys.participantInformation)}
            onChange={() =>
              handleIndividualAccordionChange("participantInformation")
            }
            isExpanded={accordionStatus.participantInformation}
          >
            <MuiBox className="sponsoring-employer">
              <ParticipantDetails
                participantDetail={participantDetail}
                preferredLanguageName={preferredLanguageName}
                deliveryCountry={deliveryCountry}
                isTMAssessmentProgram={isTMAssessmentProgram}
                isTMLeaderCoachingProgram={isTMLeaderCoachingProgram}
              />
            </MuiBox>
          </AccordionHeader>
        </PermissionWrapper>
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={renderHeader(
            programSiteCoreKeys.contactAndNotificationPreferences,
          )}
          onChange={() =>
            handleIndividualAccordionChange("contactAndNotification")
          }
          isExpanded={accordionStatus.contactAndNotification}
        >
          <MuiBox className="sponsoring-employer">
            <ContactAndNotification participantDetail={participantDetail} />
          </MuiBox>
        </AccordionHeader>

        <PermissionWrapper
          permission={
            isFromCoachPortal
              ? MODULE_PERMISSIONS.COACH_PORTAL_VIEW_ADDRESS_DETAILS
              : MODULE_PERMISSIONS.VIEW_ADDRESS_DETAILS
          }
        >
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(programSiteCoreKeys.addressDetails)}
            onChange={() => handleIndividualAccordionChange("addressDetails")}
            isExpanded={accordionStatus.addressDetails}
          >
            <MuiBox className="address-details">
              <MuiDoubleLabel
                label={fetchLabel(
                  programSiteCoreKeys.addressDetails,
                  programSiteCoreKeys.address,
                )}
                value={formatAddress()}
              />
            </MuiBox>
          </AccordionHeader>
        </PermissionWrapper>

        <PermissionWrapper
          permission={
            isFromCoachPortal
              ? MODULE_PERMISSIONS.COACH_PORTAL_VIEW_OUTREACH
              : MODULE_PERMISSIONS.VIEW_OUTREACH
          }
        >
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(programSiteCoreKeys.outreach)}
            onChange={() => handleIndividualAccordionChange("outreachDetails")}
            isExpanded={accordionStatus.outreachDetails}
          >
            <MuiBox className="sponsoring-employer">
              <OutreachDetails
                participantDetail={participantDetail}
                isCMCProgram={isCMCProgram}
                isTMAssessmentProgram={isTMAssessmentProgram}
                isTMLeaderCoachingProgram={isTMLeaderCoachingProgram}
              />
            </MuiBox>
          </AccordionHeader>
        </PermissionWrapper>

        <PermissionWrapper
          permission={
            isFromCoachPortal
              ? MODULE_PERMISSIONS.COACH_PORTAL_VIEW_SPONSORING_EMPLOYER
              : MODULE_PERMISSIONS.VIEW_SPONSORING_EMPLOYER
          }
        >
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(programSiteCoreKeys.sponsoringEmployer)}
            onChange={() =>
              handleIndividualAccordionChange("sponsoringEmployer")
            }
            isExpanded={accordionStatus.sponsoringEmployer}
          >
            <MuiBox className="sponsoring-employer">
              <SponsoringEmployer
                participantDetail={participantDetail}
                deliveryCountry={deliveryCountry}
              />
            </MuiBox>
          </AccordionHeader>
        </PermissionWrapper>

        {!isFromCoachPortal && (
          <PermissionWrapper
            permission={MODULE_PERMISSIONS.VIEW_COACH_ASSIGNMENT}
          >
            <AccordionHeader
              wrapperClassName="participant-accordion"
              title={renderHeader(coachAssignmentSiteCoreKeys.coachAssignment)}
              onChange={() =>
                handleIndividualAccordionChange("coachAssignment")
              }
              isExpanded={accordionStatus.coachAssignment}
            >
              <ViewCoachAssignment
                coachAssignmentDetails={participantDetail?.coachAssignment}
              />
            </AccordionHeader>
          </PermissionWrapper>
        )}

        {isTMLeaderCoachingProgram && (
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(goalSelectionSiteCoreKeys.goalSelectionTitle)}
            onChange={() => handleIndividualAccordionChange("goalSelection")}
            isExpanded={accordionStatus.goalSelection}
          >
            <GoalSelection
              pageId={pageId}
              programId={getValueOrBlank(programId)}
              isViewOnly
            />
          </AccordionHeader>
        )}

        {!isFromCoachPortal && isTMLeaderCoachingProgram && (
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(programSiteCoreKeys.intakeSelection)}
            onChange={() => handleIndividualAccordionChange("intakeSelection")}
            isExpanded={accordionStatus.intakeSelection}
          >
            <ParticipantIntakeSelection
              intakeDetails={participantDetail?.intakeDetails}
            />
          </AccordionHeader>
        )}
        {participantDetail?.isRegisteredInNext && (
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(programSiteCoreKeys.aboutParticipant)}
            onChange={() => handleIndividualAccordionChange("aboutParticipant")}
            isExpanded={accordionStatus.outreach}
          >
            <MuiBox className="sponsoring-employer">
              <AboutParticipant
                pageContentData={pageContentData}
                aboutParticipantNext={participantDetail?.aboutParticipantNext}
                nextPersona={nextPersona}
                preferredLanguage={participantDetail?.preferredLanguage}
                isViewProgram
              />
            </MuiBox>
          </AccordionHeader>
        )}

        <PermissionWrapper
          permission={
            isFromCoachPortal
              ? MODULE_PERMISSIONS.COACH_PORTAL_VIEW_CAREER_GOALS
              : MODULE_PERMISSIONS.VIEW_CAREER_GOALS
          }
        >
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(careerGoal)}
            onChange={() => handleIndividualAccordionChange("careerGoals")}
            isExpanded={accordionStatus.careerGoals}
          >
            <MuiBox className="sponsoring-employer">
              <CareerGoals participantDetail={participantDetail} />
            </MuiBox>
          </AccordionHeader>
        </PermissionWrapper>

        {!isFromCoachPortal && (
          <PermissionWrapper
            permission={MODULE_PERMISSIONS.VIEW_PARTICIPANT_ACTIVITY_TIMELINE}
          >
            <AccordionHeader
              wrapperClassName="participant-activity-timeline"
              title={renderHeader(activityTimeline)}
              onChange={() =>
                handleIndividualAccordionChange("participantActivityTimeline")
              }
              isExpanded={accordionStatus.participantActivityTimeline}
            >
              <MuiBox className="paticipant-activity-timeline">
                <ParticipantActivityTimeline
                  pageId={pageId}
                  isEditProgram={false}
                  programId={getValueOrBlank(programId)}
                  displayAddBtn={false}
                  nextContactDate={getValueOrBlank(
                    participantDetail?.nextContactDate,
                  )}
                />
              </MuiBox>
            </AccordionHeader>
          </PermissionWrapper>
        )}
      </TabsWrapper>
    </>
  );
};
